import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import {
  AddTaskTrackerProjectMemberRequest,
  CreateTaskTrackerProjectRequest,
  DeleteTaskTrackerProjectMemberRequest,
  MoveTaskTrackerProjectsItem,
  TaskTrackerProject,
  TaskTrackerProjectDetails,
  TaskTrackerProjectMember,
  TaskTrackerProjectRole,
  UpdateTaskTrackerProjectRequest,
  UpdateTaskTrackerRolePermissionsRequest,
} from '../store/taskTracker.types'
import { TaskTrackerSearchUsersResult } from '../lib/projectMember.types'
import { PROJECT_MEMBER_SEARCH_PER_PAGE } from '../lib/projectMember.constants'
import {
  mapCatalogUserToMemberSearchUser,
  mapMemberFromApi,
  mapProjectDetailsFromApi,
  mapProjectFromApi,
  mapRoleFromApi,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import {
  ApiProjectDetails,
  ApiProjectMember,
  ApiProjectRole,
  ApiProjectSummary,
  ApiProjectsMoveResponse,
  ApiCatalogUsersPaginated,
} from './taskTracker.api.types'

const sortProjectsByPosition = (projects: TaskTrackerProject[]) => {
  if (!projects.some((project) => project.position > 0)) return projects

  return [...projects].sort((left, right) => {
    const leftPosition = left.position > 0 ? left.position : Number.MAX_SAFE_INTEGER
    const rightPosition =
      right.position > 0 ? right.position : Number.MAX_SAFE_INTEGER

    return leftPosition - rightPosition || left.id - right.id
  })
}

export const taskTrackerProjectsApi = {
  async getProjects(): Promise<TaskTrackerProject[]> {
    const res = await API.get<ResponseWrapper<ApiProjectSummary[]>>(
      'task-tracker-projects/index',
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить проекты'))
    }
    return sortProjectsByPosition((res.data?.data ?? []).map(mapProjectFromApi))
  },

  async getProject(projectId: number): Promise<TaskTrackerProjectDetails> {
    const res = await API.get<ResponseWrapper<ApiProjectDetails>>(
      `task-tracker-projects/view${getParams({ project_id: projectId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось загрузить проект'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось загрузить проект')
    }
    return mapProjectDetailsFromApi(res.data.data)
  },

  async updateProject(
    projectId: number,
    payload: UpdateTaskTrackerProjectRequest,
  ): Promise<TaskTrackerProjectDetails> {
    const res = await API.patch<ResponseWrapper<ApiProjectDetails>>(
      `task-tracker-projects/update${getParams({ project_id: projectId })}`,
      { title: payload.title.trim() },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить название проекта'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить название проекта')
    }
    return mapProjectDetailsFromApi(res.data.data)
  },

  async createProject(
    payload: CreateTaskTrackerProjectRequest,
  ): Promise<TaskTrackerProjectDetails> {
    const res = await API.post<ResponseWrapper<ApiProjectDetails>>(
      'task-tracker-projects/create',
      {
        title: payload.title.trim(),
        members: payload.members ?? [],
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось создать проект'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось создать проект')
    }
    return mapProjectDetailsFromApi(res.data.data)
  },

  async moveProjects(
    items: MoveTaskTrackerProjectsItem[],
  ): Promise<TaskTrackerProject[] | null> {
    const res = await API.post<ResponseWrapper<ApiProjectsMoveResponse>>(
      'task-tracker-projects/move',
      { items },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось изменить порядок проектов'))
    }

    const projects = res.data?.data?.projects
    return Array.isArray(projects)
      ? sortProjectsByPosition(projects.map(mapProjectFromApi))
      : null
  },

  async addProjectMember(
    projectId: number,
    payload: AddTaskTrackerProjectMemberRequest,
  ): Promise<TaskTrackerProjectMember> {
    const res = await API.post<ResponseWrapper<ApiProjectMember>>(
      `task-tracker-projects/members-add${getParams({ project_id: projectId })}`,
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось добавить участника в проект'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось добавить участника в проект')
    }
    return mapMemberFromApi(res.data.data)
  },

  async deleteProjectMember(
    projectId: number,
    payload: DeleteTaskTrackerProjectMemberRequest,
  ): Promise<{ deleted: boolean; tasksUpdated: number }> {
    const res = await API.post<
      ResponseWrapper<{ deleted: boolean; tasks_updated: number }>
    >(
      `task-tracker-projects/members-delete${getParams({ project_id: projectId })}`,
      payload,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось удалить участника из проекта'),
      )
    }
    return {
      deleted: Boolean(res.data?.data?.deleted),
      tasksUpdated: Number(res.data?.data?.tasks_updated ?? 0),
    }
  },

  async getProjectMembers(projectId: number): Promise<TaskTrackerProjectMember[]> {
    const res = await API.get<ResponseWrapper<ApiProjectMember[]>>(
      `task-tracker-projects/members${getParams({ project_id: projectId })}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось загрузить участников проекта'),
      )
    }
    return (res.data?.data ?? []).map(mapMemberFromApi)
  },

  async updateProjectRolePermissions(
    projectId: number,
    roleId: number,
    payload: UpdateTaskTrackerRolePermissionsRequest,
  ): Promise<TaskTrackerProjectRole> {
    const res = await API.post<ResponseWrapper<ApiProjectRole>>(
      `task-tracker-projects/role-permissions-update${getParams({
        project_id: projectId,
        role_id: roleId,
      })}`,
      { permissions: payload.permissions },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить права роли'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить права роли')
    }
    return mapRoleFromApi(res.data.data)
  },

  async deleteProject(
    projectId: number,
    confirmation: string,
  ): Promise<{ deleted: boolean; projectId: number }> {
    const res = await API.delete<
      ResponseWrapper<{ deleted: boolean; project_id: number }>
    >(
      `task-tracker-projects/delete${getParams({ project_id: projectId })}`,
      {
        ...TASK_TRACKER_API_REQUEST_CONFIG,
        data: { confirmation },
      },
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить проект'))
    }
    return {
      deleted: Boolean(res.data?.data?.deleted),
      projectId: Number(res.data?.data?.project_id ?? projectId),
    }
  },

  async searchUsers(
    search: string,
    page = 1,
    perPage = PROJECT_MEMBER_SEARCH_PER_PAGE,
  ): Promise<TaskTrackerSearchUsersResult> {
    const trimmedSearch = search.trim()
    const query: Record<string, string | number> = {
      page,
      per_page: perPage,
    }

    if (trimmedSearch) {
      query.search = trimmedSearch
    }

    const res = await API.get<ResponseWrapper<ApiCatalogUsersPaginated>>(
      `task-tracker-projects/search-users${getParams(query)}`,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось выполнить поиск сотрудников'),
      )
    }

    const payload = res.data?.data
    const meta = payload?._meta

    return {
      items: (payload?.data ?? []).map(mapCatalogUserToMemberSearchUser),
      meta: {
        totalCount: Number(meta?.totalCount ?? 0),
        pageCount: Number(meta?.pageCount ?? 0),
        currentPage: Number(meta?.currentPage ?? page),
        perPage: Number(meta?.perPage ?? perPage),
      },
    }
  },
}
